class CollectionBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
    this.abortController = null;
    this.activeIndex = 0;
    this.dirtyPanels = new WeakSet();
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.tabs = Array.from(this.querySelectorAll('[data-bundle-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-bundle-panel]'));
    this.visuals = Array.from(this.querySelectorAll('[data-bundle-visual]'));
    this.purchases = Array.from(this.querySelectorAll('[data-bundle-purchase]'));
    this.features = Array.from(this.querySelectorAll('[data-bundle-features]'));
    this.switchPopup = this.querySelector('[data-bundle-switch-popup]');
    this.stickyHeader = this.querySelector('[data-bundle-sticky-header]');
    this.stickyTitle = this.querySelector('[data-bundle-sticky-title]');
    this.stickyPrice = this.querySelector('[data-bundle-sticky-price]');
    this.stickySavings = this.querySelector('[data-bundle-sticky-savings]');
    this.stickySubmit = this.querySelector('[data-bundle-sticky-submit]');
    this.currency = this.dataset.currency || 'USD';
    this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.currency });

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.requestActivation(index, true), { signal });
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event, index), { signal });
    });
    this.panels.forEach((panel) => this.initializePanel(panel, signal));
    this.querySelectorAll('[data-bundle-submit-for]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = this.panels.find((item) => item.dataset.blockId === button.dataset.bundleSubmitFor);
        if (panel) this.buyBundle(panel);
      }, { signal });
    });
    this.stickySubmit?.addEventListener('click', () => {
      const panel = this.panels[this.activeIndex];
      if (panel) this.buyBundle(panel);
    }, { signal });
    this.querySelector('[data-bundle-switch-confirm]')?.addEventListener('click', () => {
      const pendingActivation = this.pendingActivation;
      this.pendingActivation = null;
      if (!pendingActivation) return;
      this.resetPanel(this.panels[pendingActivation.index]);
      this.activateTab(pendingActivation.index, pendingActivation.focus, true);
    }, { signal });
    this.switchPopup?.querySelectorAll('[data-popup-close]:not([data-bundle-switch-confirm])').forEach((button) => {
      button.addEventListener('click', () => { this.pendingActivation = null; }, { signal });
    });
    document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    this.activateTab(this.initialPanelIndex(), false, false);
    this.initializeStickyHeader();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.stickyCleanup?.();
    this.stickyCleanup = null;
  }

  initialPanelIndex() {
    const templateIndex = this.panels.findIndex((panel) => panel.dataset.currentTemplateMatch === 'true');
    if (templateIndex >= 0) return templateIndex;

    const requestedBundle = new URL(window.location.href).searchParams.get('bundle')?.trim();
    if (requestedBundle) {
      const requestedIndex = this.panels.findIndex((panel) => panel.dataset.collectionHandle === requestedBundle);
      if (requestedIndex >= 0) return requestedIndex;
    }

    return 0;
  }

  initializeStickyHeader() {
    if (!this.stickyHeader || this.stickyCleanup) return;
    const initialize = () => {
      if (!window.VarietySectionSticky || this.stickyCleanup) return false;
      this.stickyCleanup = window.VarietySectionSticky.initSentinelSticky({
        target: this.stickyHeader,
        spacer: this.querySelector('[data-bundle-sticky-spacer]'),
        sentinel: this.querySelector('[data-bundle-sticky-sentinel]'),
        fixedClass: 'is-fixed',
        offsetVar: '--bundle-sticky-offset',
        headerSelectors: ['header.section-header']
      });
      return true;
    };
    if (!initialize()) {
      const retry = () => initialize();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', retry, { once: true, signal: this.abortController.signal });
      } else {
        requestAnimationFrame(retry);
      }
      window.addEventListener('load', retry, { once: true, signal: this.abortController.signal });
    }
  }

  initializePanel(panel, signal) {
    panel.querySelectorAll('[data-bundle-product]').forEach((product) => {
      const select = product.querySelector('[data-bundle-variant]');
      const optionSelects = product.querySelectorAll('[data-bundle-option]');
      const input = product.querySelector('[data-bundle-quantity]');
      optionSelects.forEach((optionSelect) => {
        optionSelect.addEventListener('change', () => {
          this.selectMatchingVariant(product);
          this.updateProductVariant(product);
          this.markDirty(panel);
          this.updatePanel(panel);
        }, { signal });
      });
      select?.addEventListener('change', () => {
        this.updateProductVariant(product);
        this.markDirty(panel);
        this.updatePanel(panel);
      }, { signal });
      input?.addEventListener('change', () => {
        this.normalizeQuantity(product, Number(input.value));
        this.markDirty(panel);
        this.updatePanel(panel);
      }, { signal });
      product.querySelector('[data-bundle-quantity-minus]')?.addEventListener('click', () => {
        this.normalizeQuantity(product, Number(input.value) - (Number(input.step) || 1));
        this.markDirty(panel);
        this.updatePanel(panel);
      }, { signal });
      product.querySelector('[data-bundle-quantity-plus]')?.addEventListener('click', () => {
        this.normalizeQuantity(product, Number(input.value) + (Number(input.step) || 1));
        this.markDirty(panel);
        this.updatePanel(panel);
      }, { signal });
      this.updateProductVariant(product);
    });
    this.updatePanel(panel);
  }

  markDirty(panel) { this.dirtyPanels.add(panel); }
  selectedOption(product) { const select = product.querySelector('[data-bundle-variant]'); return select?.options[select.selectedIndex] || null; }

  selectMatchingVariant(product) {
    const variantSelect = product.querySelector('[data-bundle-variant]');
    const values = Array.from(product.querySelectorAll('[data-bundle-option]')).map((select) => select.value);
    if (!variantSelect || !values.length) return;
    const matchIndex = Array.from(variantSelect.options).findIndex((option) => {
      try { return JSON.parse(option.dataset.options || '[]').every((value, index) => value === values[index]); }
      catch (error) { return false; }
    });
    variantSelect.selectedIndex = matchIndex;
  }

  syncOptionSelectors(product) {
    const selected = this.selectedOption(product);
    if (!selected?.dataset.options) return;
    try {
      const values = JSON.parse(selected.dataset.options);
      product.querySelectorAll('[data-bundle-option]').forEach((select, index) => { select.value = values[index] || ''; });
    } catch (error) {
      // Keep the Liquid-selected option values if variant data cannot be parsed.
    }
  }

  effectiveRules(product, option = this.selectedOption(product)) {
    const minimum = Math.max(1, Number(product.dataset.bundleMinimum) || 1, Number(option?.dataset.min) || 1);
    const increment = Math.max(1, Number(option?.dataset.increment) || 1);
    const rawMaximum = option?.dataset.max;
    const maximum = rawMaximum === undefined || rawMaximum === '' ? null : Math.max(0, Number(rawMaximum));
    return { minimum, increment, maximum };
  }

  normalizeQuantity(product, requested) {
    const input = product.querySelector('[data-bundle-quantity]');
    const { minimum, increment, maximum } = this.effectiveRules(product);
    let value = Number.isFinite(requested) ? Math.max(minimum, requested) : minimum;
    value = minimum + Math.ceil((value - minimum) / increment) * increment;
    if (maximum !== null) value = Math.min(maximum, value);
    input.min = String(minimum);
    input.step = String(increment);
    if (maximum === null) input.removeAttribute('max'); else input.max = String(maximum);
    input.value = String(value);
    const unavailable = maximum !== null && maximum < minimum;
    const minus = product.querySelector('[data-bundle-quantity-minus]');
    const plus = product.querySelector('[data-bundle-quantity-plus]');
    if (minus) minus.disabled = unavailable || value <= minimum;
    if (plus) plus.disabled = unavailable || (maximum !== null && value >= maximum);
    input.disabled = unavailable;
    return value;
  }

  updateProductVariant(product) {
    const option = this.selectedOption(product);
    if (!option) return;
    product.classList.toggle('is-unavailable', option.dataset.available !== 'true');
    const image = product.querySelector('.bundle-builder__product-image');
    if (image && option.dataset.imageSrc) { image.src = option.dataset.imageSrc; image.srcset = ''; image.alt = option.dataset.imageAlt || ''; }
    this.normalizeQuantity(product, Number(product.querySelector('[data-bundle-quantity]')?.value));
  }

  updatePanel(panel) {
    const discount = Math.max(0, Math.min(100, Number(panel.dataset.discount) || 0));
    const purchase = this.purchases?.find((item) => item.dataset.blockId === panel.dataset.blockId);
    let originalTotal = 0;
    let discountedTotal = 0;
    let invalid = false;
    let quantitiesAtMinimum = true;
    panel.querySelectorAll('[data-bundle-product]').forEach((product) => {
      const option = this.selectedOption(product);
      if (!option || option.dataset.available !== 'true') invalid = true;
      const price = Number(option?.dataset.price) || 0;
      const quantity = this.normalizeQuantity(product, Number(product.querySelector('[data-bundle-quantity]')?.value));
      const bundleMinimum = Math.max(1, Number(product.dataset.bundleMinimum) || 1);
      if (quantity !== bundleMinimum) quantitiesAtMinimum = false;
      if (product.querySelector('[data-bundle-quantity]')?.disabled) invalid = true;
      originalTotal += price * quantity;
      discountedTotal += this.discountedUnitPrice(price, discount) * quantity;
      const original = product.querySelector('[data-bundle-original-unit]');
      if (original) original.textContent = this.formatMoney(price);
    });
    const salePrice = Number(panel.dataset.salePrice);
    if (quantitiesAtMinimum && Number.isFinite(salePrice) && salePrice > 0) discountedTotal = Math.round(salePrice);
    const savingsTotal = Math.max(0, originalTotal - discountedTotal);
    const choicePrice = this.querySelector(`[data-choice-price="${panel.dataset.blockId}"]`);
    const choiceSavings = this.querySelector(`[data-choice-savings="${panel.dataset.blockId}"]`);
    if (choicePrice) choicePrice.textContent = this.formatMoney(discountedTotal);
    if (choiceSavings) choiceSavings.textContent = this.formatMoney(savingsTotal);
    if (purchase) {
      const total = purchase.querySelector('[data-bundle-total]');
      const savings = purchase.querySelector('[data-bundle-total-savings]');
      if (total) total.textContent = this.formatMoney(discountedTotal);
      if (savings) savings.textContent = this.formatMoney(savingsTotal);
      const submit = purchase.querySelector('[data-bundle-submit-for]');
      if (submit) { submit.disabled = invalid; submit.setAttribute('aria-disabled', String(invalid)); }
    }
    if (panel === this.panels[this.activeIndex]) this.updateStickyHeader(panel, originalTotal, discountedTotal, invalid);
  }

  updateStickyHeader(panel, originalTotal, discountedTotal, invalid) {
    if (!this.stickyHeader || !panel) return;
    if (this.stickyTitle) this.stickyTitle.textContent = panel.dataset.bundleName || '';
    if (this.stickyPrice) this.stickyPrice.textContent = this.formatMoney(discountedTotal);
    if (this.stickySavings) this.stickySavings.textContent = `${this.dataset.savingsLabel || 'Saved'} ${this.formatMoney(Math.max(0, originalTotal - discountedTotal))}`;
    if (this.stickySubmit) {
      this.stickySubmit.disabled = invalid;
      this.stickySubmit.setAttribute('aria-disabled', String(invalid));
    }
  }

  resetPanel(panel) {
    panel.querySelectorAll('[data-bundle-product]').forEach((product) => {
      const select = product.querySelector('[data-bundle-variant]');
      if (select) {
        const availableIndex = Array.from(select.options).findIndex((option) => option.dataset.available === 'true');
        select.selectedIndex = availableIndex >= 0 ? availableIndex : 0;
        this.syncOptionSelectors(product);
      }
      const input = product.querySelector('[data-bundle-quantity]');
      if (input) input.value = input.dataset.initialQuantity || product.dataset.bundleMinimum || '1';
      this.updateProductVariant(product);
    });
    this.dirtyPanels.delete(panel);
    this.updatePanel(panel);
  }

  requestActivation(index, focus) {
    if (index === this.activeIndex) return;
    const current = this.panels[this.activeIndex];
    if (current && this.dirtyPanels.has(current)) {
      this.pendingActivation = { index, focus };
      this.switchPopup?.modalPopupOpen?.(`data-bundle-switch-popup-${this.dataset.sectionId}`);
      return;
    }
    this.resetPanel(this.panels[index]);
    this.activateTab(index, focus, true);
  }

  activateTab(index, focus, scroll) {
    if (!this.tabs[index] || !this.panels[index]) return;
    this.activeIndex = index;
    this.tabs.forEach((tab, i) => { const active = i === index; tab.classList.toggle('is-active', active); tab.setAttribute('aria-selected', String(active)); tab.setAttribute('aria-expanded', String(active)); tab.tabIndex = active ? 0 : -1; });
    [this.panels, this.visuals, this.purchases].forEach((group) => group.forEach((item, i) => { item.hidden = i !== index; item.classList.toggle('is-active', i === index); }));
    this.features.forEach((item) => {
      const active = item.dataset.blockId === this.panels[index].dataset.blockId;
      item.hidden = !active;
      item.classList.toggle('is-active', active);
    });
    this.updatePanel(this.panels[index]);
    if (scroll) this.tabs[index].scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', inline: 'nearest', block: 'nearest' });
    if (focus) this.tabs[index].focus();
  }

  handleTabKeydown(event, index) {
    let next;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index + 1;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else return;
    event.preventDefault();
    this.requestActivation((next + this.tabs.length) % this.tabs.length, true);
  }

  // Round each unit's discount down to whole cents before multiplying by quantity.
  discountedUnitPrice(cents, discount) { return cents - Math.floor(cents * discount / 100); }
  formatMoney(cents) { return this.moneyFormatter.format(cents / 100); }

  buyBundle(panel) {
    const purchase = this.purchases.find((item) => item.dataset.blockId === panel.dataset.blockId);
    const submit = purchase?.querySelector('[data-bundle-submit-for]');
    const status = purchase?.querySelector('[data-bundle-status-for]');
    const items = [];
    for (const product of panel.querySelectorAll('[data-bundle-product]')) {
      const option = this.selectedOption(product);
      if (!option || option.dataset.available !== 'true') { this.showStatus(status, this.dataset.errorOption); return; }
      const quantity = this.normalizeQuantity(product, Number(product.querySelector('[data-bundle-quantity]')?.value));
      const { minimum, maximum } = this.effectiveRules(product, option);
      if (!Number.isInteger(quantity) || quantity < minimum || (maximum !== null && quantity > maximum)) { this.showStatus(status, this.dataset.errorQuantity); return; }
      items.push({ id: Number(option.value), quantity });
    }
    if (!items.length) { this.showStatus(status, this.dataset.errorEmpty); return; }
    submit.disabled = true;
    submit.setAttribute('aria-disabled', 'true');
    this.showStatus(status, this.dataset.statusAdding, false);
    try {
      const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
      const key = `${this.dataset.sectionId}-${panel.dataset.blockId}-${Date.now()}`;
      const params = new URLSearchParams();
      if (panel.dataset.discountCode?.trim()) params.set('discount', panel.dataset.discountCode.trim());
      params.set('attributes[_bundle_key]', key);
      params.set('attributes[_bundle_collection]', panel.dataset.collectionHandle || '');
      params.set('attributes[_bundle_discount]', `${panel.dataset.discount}%`);
      window.location.assign(`${root}/cart/${items.map((item) => `${item.id}:${item.quantity}`).join(',')}?${params}`);
    } catch (error) {
      this.showStatus(status, this.dataset.errorAdd);
      submit.disabled = false;
      submit.setAttribute('aria-disabled', 'false');
    }
  }

  showStatus(element, message, error = true) { if (element) { element.textContent = message || ''; element.classList.toggle('is-success', !error); } }

  handleBlockSelect = (event) => {
    if (!this.contains(event.target)) return;
    const blockId = event.detail?.blockId || event.target.dataset.blockId;
    const index = this.panels.findIndex((panel) => panel.dataset.blockId === blockId);
    if (index >= 0) this.activateTab(index, false, false);
  };
}

if (!customElements.get('collection-bundle-builder')) customElements.define('collection-bundle-builder', CollectionBundleBuilder);
